<template>
	<div class="container__registers">
		<ContentHeader>
			<SubHeader :title="`${$t('exports')} / ${$t('plotInventory')}`" />
		</ContentHeader>
		<ContentBody>
			<Card>
				<div class="container__crud">
					<div class="container__filtros">
						<MultiSelect
							v-model="fazendasEscolhidas"
							:label="$t('farm') + '*'"
							:options="fazendas"
						/>

						<MultiSelect
							v-model="estratosEscolhidos"
							:label="$t('forestStratification')"
							:options="estratos"
						/>

						<div class="container__filtros__campo">
							<DatePickerRange
								v-model="intervaloMedicao"
								:label="$t('measurementRange')"
								:locale="localeDoSistema"
							/>
						</div>

						<div class="container__filtros__campo">
							<label class="container__filtros__label">{{ $t('inventoryType') }}</label>
							<CheckboxGroup
								v-model="selectedExportOptions"
								class="container__filtros__checkboxes"
								:items="labels"
							/>
						</div>
					</div>

					<div class="container__acoes">
						<Button
							:label="$t('clearFilters')"
							color="trv-default"
							semantic-class="tiny"
							outlined
							width-auto
							:disabled="filtrando"
							@click="limparFiltros"
						/>
						<Button
							:label="$t('filterData')"
							color="trv-default"
							semantic-class="tiny"
							width-auto
							:loading="filtrando"
							:disabled="!podeFiltrar"
							@click="filtrar"
						/>
					</div>

					<!-- A tabela já traz busca, paginação e o estado vazio -->
					<div class="container__tabela">
						<BaseTableWithPagination
							:key="tabelaKey"
							:data="data"
							:colunas="colunas"
							@update:checked="handleSelectionChange"
						/>
					</div>

					<div
						v-if="data.length > 0"
						class="container__acoes"
					>
						<span
							v-if="selectedItems.length === 0"
							class="container__acoes__dica"
						>
							{{ $t('selecioneAoMenosUmProcessamentoParaExportar') }}
						</span>
						<Button
							:label="$t('export')"
							color="trv-default"
							semantic-class="tiny"
							width-auto
							:loading="exportando"
							:disabled="selectedItems.length === 0 || exportando"
							@click="exportar"
						/>
					</div>
				</div>
			</Card>
		</ContentBody>
	</div>
</template>

<script>
import Button from '../../../components/Button/Button'
import Card from '../../../components/Card/Card'
import CheckboxGroup from '../../../components/CheckboxGroup/CheckBoxGroup'
import ContentBody from '../../../components/ContentBody/ContentBody'
import ContentHeader from '../../../components/ContentHeader/ContentHeader'
import DatePickerRange from '../../../components/DatePickerRange/DatePickerRange.vue'
import MultiSelect from '../../../components/MultiSelect/MultiSelect'
import SubHeader from '../../../components/SubHeader/SubHeader'
import BaseTableWithPagination from '../../../components/BaseTableWithPagination/BaseTableWithPagination'
import {
	getParcelaCsv,
	getDropdownFazendaEstratoFlorestal,
	BuscaFiltros,
} from '../../../services/requests/ExportacaoRequests'
import { useMessagesStore } from '../../../store/modules/moduleMessages.js'

const messagesStore = useMessagesStore()

export default {
	components: {
		BaseTableWithPagination,
		Button,
		Card,
		CheckboxGroup,
		ContentBody,
		ContentHeader,
		DatePickerRange,
		MultiSelect,
		SubHeader,
	},
	data() {
		return {
			filtrando: false,
			exportando: false,
			/**
			 * Remonta a tabela quando o resultado é substituído ou limpo: a seleção vive
			 * dentro do BaseTableWithPagination e, sem isso, ids marcados de uma busca
			 * anterior continuariam visíveis enquanto `selectedItems` já foi zerado.
			 */
			tabelaKey: 0,
			dropdown: [],
			data: [],
			fazendasEscolhidas: [],
			estratosEscolhidos: [],
			selectedItems: [],
			intervaloMedicao: null,
			labels: [
				{
					id: 'install',
					label: this.$t('instalationInventory'),
					value: 'Inventário de Instalação',
				},
				{ id: 'continuous', label: this.$t('continueInventory'), value: 'Inventário Contínuo' },
				{
					id: 'precut',
					label: this.$t('inventarioPreCorte'),
					value: 'Inventário Pré-Corte',
					disabled: false,
				},
			],
			selectedExportOptions: [],
			colunas: [
				{ nome: this.$t('code'), key: 'processamentoCodigo', enableSort: true },
				{
					nome: this.$t('descricaoDoProcessamento'),
					key: 'processamentoDescricao',
					enableSort: true,
				},
				{
					nome: this.$t('processingDate'),
					key: 'processamentoData',
					tipo: 'date',
					enableSort: true,
				},
				{ nome: 'Status', key: 'processamentoOficial', enableSort: true },
				{ nome: this.$t('codigoDeMedicao'), key: 'medicaoCodigo', enableSort: true },
				{
					nome: this.$t('descricaoDaMedicao'),
					key: 'medicaoDescricao',
					tipo: 'date',
					enableSort: true,
				},
				{ nome: this.$t('dataDaColeta'), key: 'dataColeta', tipo: 'date', enableSort: true },
				{ nome: this.$t('inventoryType'), key: 'tipoServicoDescricao', enableSort: true },
			],
		}
	},
	computed: {
		fazendas() {
			const mapa = new Map()
			return this.dropdown
				.filter((m) => {
					if (!mapa.has(m.fazendaNome)) {
						mapa.set(m.fazendaNome, true)
						return true
					}
					return false
				})
				.map((m) => Object.assign({}, m, { label: m.fazendaNome, value: m.fazendaId }))
		},
		estratos() {
			const estratos = []
			const fazendas = this.dropdown.filter((f) => this.fazendasEscolhidas.includes(f.fazendaId))

			fazendas.forEach((el) => {
				estratos.push({ label: el.estratoFlorestalDescricao, value: el.estratoFlorestalId })
			})

			return estratos
		},
		/** Fazenda é obrigatória para a busca */
		podeFiltrar() {
			return !this.filtrando && this.fazendasEscolhidas.length > 0
		},
		localeDoSistema() {
			const idioma = this.$i18n.locale
			if (idioma === 'pt') return 'pt-BR'
			if (idioma === 'es') return 'es'
			return 'en'
		},

		dadoFormatado() {
			const formattedData = {
				TipoExportacao: 'Default',
				Processamento: this.selectedItems.map((item) => ({
					EstratoInventarioFlorestalId: item.id,
					Codigo: item.processamentoCodigo.toString(),
					Descricao: item.processamentoDescricao,
				})),
			}

			return formattedData
		},
	},
	watch: {
		/**
		 * O MultiSelect procura o label do primeiro valor selecionado nas opções e quebra
		 * se não o encontrar; ao remover uma fazenda, os estratos dela saem da seleção.
		 */
		estratos(opcoes) {
			const validos = this.estratosEscolhidos.filter((id) => opcoes.some((o) => o.value === id))
			if (validos.length !== this.estratosEscolhidos.length) this.estratosEscolhidos = validos
		},
	},
	async created() {
		try {
			const response = await getDropdownFazendaEstratoFlorestal()
			this.dropdown = response.data ?? []
		} catch {
			messagesStore.commit('mutationShowMessage', {
				messageTitle: this.$t('error'),
				messageText: this.$t('erroAoBuscarDadosExportacao'),
				messageType: 'error',
			})
		}
	},
	methods: {
		async exportar() {
			// O botão já fica desabilitado sem seleção; a guarda cobre chamadas programáticas.
			if (this.selectedItems.length === 0 || this.exportando) return

			this.exportando = true
			try {
				const response = await getParcelaCsv(this.dadoFormatado)
				var a = document.createElement('a')
				a.target = '_blank'
				a.href = response.data
				document.body.appendChild(a)
				a.click()
				document.body.removeChild(a)
			} finally {
				this.exportando = false
			}
		},

		async filtrar() {
			// O botão já fica desabilitado sem fazenda; a guarda cobre chamadas programáticas.
			if (!this.podeFiltrar) return

			try {
				this.filtrando = true
				const payload = {
					FazendasIds: this.fazendasEscolhidas ?? [],
					EstratoFlorestaisIds: this.estratosEscolhidos ?? [],
					DataMedicaoInicial: this.intervaloMedicao?.start?.toISOString() ?? null,
					DataMedicaoFinal: this.intervaloMedicao?.end?.toISOString() ?? null,
					TipoProcessamentoFlorestal: this.selectedExportOptions ?? [],
				}

				const response = await BuscaFiltros(payload)

				if (response.data.length === 0) {
					this.filtrando = false
					messagesStore.commit('mutationShowMessage', {
						messageTitle: this.$t('atention'),
						messageText: this.$t('selectedFilter'),
						messageIcon: 'fas fa-exclamation-circle',
						messageType: 'warning',
					})
					return
				}
				this.data = response.data
				this.resetarSelecao()
				this.filtrando = false
			} catch {
				this.filtrando = false
				messagesStore.commit('mutationShowMessage', {
					messageTitle: this.$t('error'),
					messageText: this.$t('anErrorOcurredSearchingProcessing'),
					messageIcon: 'far fa-check-circle',
					messageType: 'error',
				})
			}
		},
		handleSelectionChange(newSelection) {
			this.selectedItems = newSelection.selectedItems
		},
		resetarSelecao() {
			this.selectedItems = []
			this.tabelaKey++
		},
		limparFiltros() {
			this.fazendasEscolhidas = []
			this.estratosEscolhidos = []
			this.selectedExportOptions = []
			this.intervaloMedicao = null
			this.data = []
			this.resetarSelecao()
		},
	},
}
</script>

<style scoped src="../../../css/crud.css"></style>

<style scoped>
/* Mesma disposição da exportação a nível de árvore: campos lado a lado, 20px entre eles */
.container__filtros {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	column-gap: 20px;
	row-gap: 0.5rem;
}

.container__filtros__campo {
	display: flex;
	flex-direction: column;
	margin-top: 0.5rem;
}

/* Labels e caixas replicam o MultiSelect (label navy 0.8rem, caixa de 2rem com borda cinza) */
.container__filtros__label,
.container__filtros__campo :deep(.form-label) {
	color: var(--color-navy-blue);
	font-size: 0.8rem;
	font-weight: 400;
	line-height: 1.5;
	margin: 0;
}

/* A raiz do v-date-picker é um <span> inline, que desalinhava o label em relação ao vizinho */
.container__filtros__campo > span {
	display: flex;
	flex-direction: column;
}

.container__filtros__campo :deep(.datepicker) {
	height: 2rem;
	border: 1px solid rgb(var(--color-gray));
	border-radius: 4.5px;
	overflow: hidden;
}

.container__filtros__campo :deep(.datepicker i) {
	display: flex;
	align-items: center;
	padding: 0 0.75rem;
	border: none;
	border-radius: 0;
}

.container__filtros__campo :deep(.datepicker .ui.input),
.container__filtros__campo :deep(.datepicker input) {
	width: 100%;
	height: 100%;
}

.container__filtros__campo :deep(.datepicker input) {
	border: none;
	outline: none;
	padding: 0 1rem;
	font-size: 0.75rem;
	color: var(--color-navy-blue);
}

.container__filtros__checkboxes {
	display: flex;
	flex-wrap: wrap;
	column-gap: 1.5rem;
	row-gap: 0.5rem;
	min-height: 2rem;
	align-items: center;
}

.container__acoes {
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 0.75rem;
	margin-top: 1rem;
}

.container__acoes__dica {
	font-size: 0.8rem;
	color: var(--color-navy-blue);
}

.container__tabela {
	margin-top: 1.5rem;
}

@media screen and (max-width: 767px) {
	.container__filtros {
		grid-template-columns: minmax(0, 1fr);
	}
}
</style>
